import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm, FormProvider, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { usePlaidLink } from "react-plaid-link";
import { useNavigate, useLocation } from "react-router-dom";
import { useFlags } from "launchdarkly-react-client-sdk";

import Text from "components/common/Text";
import Button from "components/common/Button";
import { usePermissions } from "components/Hook";
import RadioButton from "components/common/RadioButtons";
import CustomTooltip from "components/common/Tooltip";
import CustomSnackbar, { AlertType } from "components/common/Snackbar";
import PageLoader from "components/common/PageLoader";
import AddPaymentLoaderModal from "components/Screens/CustomerOnboarding/AddPaymentLoaderModal";

import { AddAchInfo, ResetAddAchInfo } from "store/actions/Payment/AddAchInfo";
import { AddCustomerBankInfo, ResetAddCustomerBankInfo } from "store/actions/Payment/AddBankAccountInfo";
import { CustomerBankInfo } from "store/actions/Payment/GetCustomerBankInfo";

import AddAchPaymentSchemaNew from "validations/Onboarding/AddAchPaymentSchemaNew";

import { baseUrl } from "util/APIBaseUrl";
import { showSnackbar } from "store/actions/Utility";

import "components/Screens/CustomerOnboarding/AddPaymentMethod/AddPaymentMethod.scss";

import {
  InfoOutlined as InfoOutlinedIcon,
  LockOutlined as LockOutlinedIcon,
  InsertLink as InsertLinkIcon,
} from "@mui/icons-material";

import "./AddPayment.scss";

function PaymentAch({ update, change }) {
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const { loggedInUser } = usePermissions();

  const getButtonLabel = () => {
    if (update) return "Update payment method";
    if (change) return "Change payment method";
    return "Add payment method";
  };

  const buttonLabel = getButtonLabel();

  const [linkToken, setLinkToken] = useState(null);
  const [plaidClosed, setPlaidClosed] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState(null);
  const [loading, setLoading] = useState(false);

  const { stripeFc = false } = useFlags();

  const loadingAddAchBankInfo = useSelector((globalState) => globalState.AddAchInfo.loadingAddAchBankInfo);
  const isAddAchInfoSuccess = useSelector((globalState) => globalState.AddAchInfo?.addAchBankInfo?.ok);
  const addBankAccountInfo = useSelector((globalState) => globalState.AddBankAccountInfo.addBankAccountInfo);

  const methods = useForm({
    resolver: yupResolver(AddAchPaymentSchemaNew),
    mode: "onChange",
  });

  const handleFieldChange = (value, field) => {
    field.onChange(value);
  };

  const getLinkToken = async () => {
    const accessToken = localStorage.getItem("accessToken");
    if (accessToken != null && accessToken != undefined) {
      const response = await fetch(`${baseUrl}/getLinkToken`, {
        method: "get",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (!response.ok) {
        return;
      }
      const data = await response.json();

      setLinkToken(data);
    }
  };

  const onSuccess = useCallback(
    (publicToken) => {
      setPlaidClosed(false);

      const setToken = async () => {
        const userAdditionalInfo = JSON.parse(sessionStorage.getItem("userAdditionalInfo"));
        const body = {
          customerID: loggedInUser?.userId,
          publicToken,
        };

        const accessToken = localStorage.getItem("accessToken");
        if (accessToken != null && accessToken != undefined) {
          dispatch(
            AddCustomerBankInfo({
              request: `${baseUrl}/customer/${userAdditionalInfo?.customerId}/account`,
              body: JSON.stringify(body),
            })
          );
        }
      };
      setToken();

      navigate("/main/payment");
    },
    [loggedInUser?.userId, dispatch, navigate]
  );

  const config = {
    token: linkToken?.link_token,
    onSuccess,
    onExit: () => setPlaidClosed(true),
  };

  const { open } = usePlaidLink(config);

  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = methods;

  const onSubmit = async (data = {}) => {
    try {
      dispatch(
        AddAchInfo({
          request: `${baseUrl}/customer/${loggedInUser?.customerId}/account/manual`,
          body: {
            accountHolderType: data?.accountHolderType,
            accountType: data?.accountType,
            accountHolderName: data?.accountHolderName,
            accountNumber: data?.accountNumber,
            routingNumber: data?.routingNumber,
          },
          hidePlaidBanner: false,
        })
      );
    } catch (error) {
      setSnackbarMessage("ACH update failed");
    }
  };

  useEffect(() => {
    if (isAddAchInfoSuccess) {
      dispatch(ResetAddAchInfo({}));
      navigate("/main/payment");
    }
  }, [isAddAchInfoSuccess, dispatch, navigate]);

  useEffect(() => {
    if (addBankAccountInfo?.ok) {
      setLoading(false);
      dispatch(ResetAddCustomerBankInfo({}));
      navigate("/main/payment");
    } else if (addBankAccountInfo?.error || typeof addBankAccountInfo === "string") {
      setLoading(false);
      dispatch(
        showSnackbar({
          dispatch,
          type: AlertType.error,
          message: addBankAccountInfo?.error?.[0]?.description || "Failed to add payment method. Please try again.",
        })
      );
      dispatch(ResetAddCustomerBankInfo({}));
    }
  }, [addBankAccountInfo, dispatch, navigate]);

  useEffect(() => {
    if (!stripeFc) {
      getLinkToken();
    }
  }, [stripeFc]);

  useEffect(() => {
    dispatch(ResetAddCustomerBankInfo({}));
    dispatch(
      CustomerBankInfo({
        request: `${baseUrl}/customer/${loggedInUser?.customerId}/bankInfo`,
      })
    );
  }, [loggedInUser, dispatch]);

  useEffect(() => {
    if (state?.AddPaymentMethodByDefault) {
      open();
    }
  }, [state, open]);

  return (
    <div className="payment-content-container">
      <p className="heading-h3-semibold mb-1">Enter your bank account details</p>

      <FormProvider {...methods}>
        <form className="payment-form-container" onSubmit={handleSubmit(onSubmit)}>
          <div className="payment-input-group">
            <p className="para-body-m-medium text-headings-secondary">Account holder type</p>
            <div className="flex gap-1 flex-nowrap">
              <Controller
                name="accountHolderType"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <RadioButton
                    label="Company"
                    value="company"
                    withContainer
                    checked={field.value === "company"}
                    onChange={() => handleFieldChange("company", field)}
                  />
                )}
              />
              <Controller
                name="accountHolderType"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <RadioButton
                    withContainer
                    label="Individual"
                    value="individual"
                    checked={field.value === "individual"}
                    onChange={() => handleFieldChange("individual", field)}
                  />
                )}
              />
            </div>
          </div>
          <div className="payment-input-group">
            <p className="para-body-m-medium text-headings-secondary mt-15">Account type</p>
            <div className="flex gap-1 flex-nowrap">
              <Controller
                name="accountType"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <RadioButton
                    withContainer
                    label="Checking"
                    value="Checking"
                    checked={field.value === "checking"}
                    onChange={() => handleFieldChange("checking", field)}
                  />
                )}
              />
              <Controller
                name="accountType"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <RadioButton
                    withContainer
                    label="Savings"
                    value="Savings"
                    checked={field.value === "savings"}
                    onChange={() => handleFieldChange("savings", field)}
                  />
                )}
              />
            </div>
          </div>
          <div className="mt-15">
            <Text label="Account holder name" name="accountHolderName" isPayment />
          </div>
          <div className="input-group-add-payment">
            <Text
              label="Account number"
              labelIcon={
                <CustomTooltip content="Your account number is the unique identifier assigned to your bank account.">
                  <InfoOutlinedIcon className="onboarding-add-payment-label-icon" />
                </CustomTooltip>
              }
              name="accountNumber"
              isPayment
            />
            <Text
              label="ACH routing number"
              labelIcon={
                <CustomTooltip content="Your ACH routing number is a 9-digit code that identifies your bank and branch.">
                  <InfoOutlinedIcon className="onboarding-add-payment-label-icon" />
                </CustomTooltip>
              }
              isPayment
              name="routingNumber"
            />
          </div>

          {stripeFc && (
            <p className="mandate-text-inline" style={{ marginTop: "16px" }}>
              By clicking <strong>{buttonLabel}</strong>, you authorize Edge to debit the bank account specified above
              for any amount owed for charges arising from your use of Edge&apos;s services, until this authorization is
              revoked.
            </p>
          )}

          {!stripeFc && (
            <div>
              <Button
                type="button"
                onClick={() => open()}
                name="connectViaPlaid"
                variant="text"
                size="medium"
                startIcon={<InsertLinkIcon />}
                label="Or Connect Your Bank via Plaid"
                isFullWidth={false}
              />
            </div>
          )}
        </form>
      </FormProvider>

      {stripeFc && (
        <p className="para-body-s-regular color-text-body-primary mt-1">
          Your payment information is encrypted and securely shared with Stripe for processing. We never store your card
          or bank account information.
        </p>
      )}

      <div className="mt-125 w-full">
        <Button
          onClick={handleSubmit(onSubmit)}
          name={buttonLabel}
          size="large"
          label={buttonLabel}
          startIcon={<LockOutlinedIcon />}
          isFullWidth
          isDisabled={!isValid}
          isLoading={loadingAddAchBankInfo}
        />
      </div>

      {plaidClosed && (
        <div className="mt-1">
          <span className="plaid-link-close-desc">Can’t find your bank on Plaid? </span>
          <span className="plaid-link-close-btn"> Add ACH details manually.</span>
        </div>
      )}
      {loading && <PageLoader />}
      {loadingAddAchBankInfo && (
        <AddPaymentLoaderModal
          open={loadingAddAchBankInfo}
          title="Hang tight, we're adding your payment method…"
          description="Just a moment while we securely save your payment details. This won't take long."
        />
      )}
      <CustomSnackbar
        open={!!snackbarMessage}
        message={snackbarMessage}
        onClose={() => setSnackbarMessage(null)}
        type="error"
      />
    </div>
  );
}

export default PaymentAch;
